import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../button/Button";
import CodeInput, { CodeInputHandle } from "../CodeInput/CodeInput";
import styles from "./LogInCodeForm.module.scss";
import checkbox from "../../assets/icons/checkbox.svg";
import arrow from "../../assets/icons/arrow-right.svg";
import { useLoginCodeRequestMutation, useLoginCodeVerifyMutation } from "../../store/services/auth-api";
import { getApiErrorMessage } from "../../utils/codeLogin";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const emptyCode = () => Array<string>(CODE_LENGTH).fill("");

interface LogInCodeFormProps {
  email: string;
  emailMasked: string;
  loginTicket: string;
  onChangeEmail: () => void;
}

// Login step 2 for email-code accounts: the 6-digit code sent by email
const LogInCodeForm: React.FC<LogInCodeFormProps> = ({ email, emailMasked, loginTicket, onChangeEmail }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loginCodeVerify] = useLoginCodeVerifyMutation();
  const [loginCodeRequest] = useLoginCodeRequestMutation();
  const codeInputRef = useRef<CodeInputHandle>(null);
  const isSubmittingRef = useRef(false);

  const [code, setCode] = useState<string[]>(emptyCode);
  const [ticket, setTicket] = useState(loginTicket);
  const [maskedEmail, setMaskedEmail] = useState(emailMasked);
  const [checked, setChecked] = useState(true);
  const [isLoader, setIsLoader] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCoolDown, setResendCoolDown] = useState(RESEND_COOLDOWN_SECONDS); // a code was just sent
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  useEffect(() => {
    if (resendCoolDown <= 0) return;
    const timerId = setTimeout(() => setResendCoolDown((prev) => prev - 1), 1000);
    return () => clearTimeout(timerId);
  }, [resendCoolDown]);

  const verifyCode = async (value: string) => {
    if (isSubmittingRef.current) return;
    if (value.length !== CODE_LENGTH) {
      setInfoMsg("");
      setErrorMsg(t("Please enter the 6-digit code."));
      codeInputRef.current?.focus();
      return;
    }

    isSubmittingRef.current = true;
    setErrorMsg("");
    setInfoMsg("");
    setIsLoader(true);
    try {
      // Token and user are stored by the endpoint, exactly like the password login
      await loginCodeVerify({ email, code: value, loginTicket: ticket, keepLogin: checked }).unwrap();
      navigate("/");
    } catch (error) {
      isSubmittingRef.current = false;
      setIsLoader(false);
      setErrorMsg(getApiErrorMessage(error, t("Something went wrong. Please try again.")));
      setCode(emptyCode());
      codeInputRef.current?.focus();
    }
  };

  const resendCode = async () => {
    if (resendCoolDown > 0 || isResending || isLoader) return;

    setIsResending(true);
    setErrorMsg("");
    setInfoMsg("");
    try {
      const payload = await loginCodeRequest({ email }).unwrap();
      setTicket(payload.loginTicket);
      if (payload.emailMasked) setMaskedEmail(payload.emailMasked);
      setCode(emptyCode());
      setInfoMsg(t("We sent you a new code."));
      setResendCoolDown(RESEND_COOLDOWN_SECONDS);
      codeInputRef.current?.focus();
    } catch (error) {
      setErrorMsg(getApiErrorMessage(error, t("Something went wrong. Please try again.")));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <form
      className={styles.root}
      onSubmit={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
          e.preventDefault();
          verifyCode(code.join(""));
        }
      }}
      noValidate
    >
      <button type="button" className={styles.root_back} onClick={onChangeEmail}>
        <img src={arrow} className={styles.root_back_icon} alt="" />
        {t("Use a different email")}
      </button>

      <div className={styles.root_header}>
        <h1 className={styles.root_header_title}>{t("Check your email")}</h1>
        <p className={styles.root_header_subtitle}>
          {t("We sent a 6-digit code to {{email}}. It is valid for 10 minutes. Check your spam folder too.", {
            email: maskedEmail,
          })}
        </p>
      </div>

      <div className={styles.root_code}>
        <CodeInput
          ref={codeInputRef}
          values={code}
          onChange={setCode}
          onComplete={verifyCode}
          ariaLabel={t("6-digit code")}
          autoFocus
          readOnly={isLoader}
          error={errorMsg !== ""}
        />
        {errorMsg && (
          <p className={styles.root_code_error} role="alert">
            {errorMsg}
          </p>
        )}
        {infoMsg && (
          <p className={styles.root_code_info} role="status">
            {infoMsg}
          </p>
        )}
      </div>

      <div className={styles.root_footer}>
        <Button
          classNames={styles.root_footer_button}
          onClick={() => verifyCode(code.join(""))}
          text={t("Log in")}
          isLoader={isLoader}
        />
        <div className={styles.root_footer_actions}>
          <button
            type="button"
            role="checkbox"
            aria-checked={checked}
            className={styles.root_footer_actions_keep}
            onClick={() => setChecked(!checked)}
          >
            {checked ? (
              <img src={checkbox} alt="" className={styles.root_footer_actions_keep_img} />
            ) : (
              <span className={styles.root_footer_actions_keep_checkbox} />
            )}
            {t("Keep me signed in")}
          </button>
          <button
            type="button"
            className={styles.root_footer_actions_resend}
            onClick={resendCode}
            disabled={resendCoolDown > 0 || isResending}
          >
            {resendCoolDown > 0 ? t("Resend code in {{seconds}}s", { seconds: resendCoolDown }) : t("Resend code")}
          </button>
        </div>
      </div>
    </form>
  );
};

export default LogInCodeForm;
